import { createApp } from 'vue'
import mapMixins from './maps.js'
import { cloneDeep } from 'lodash'
import { Modal } from 'bootstrap'
import swal from 'sweetalert'
const { Map } = await google.maps.importLibrary('maps')
const { LatLngBounds } = await google.maps.importLibrary('core')
const { AdvancedMarkerElement } = await google.maps.importLibrary('marker')

window.pawine = window.pawine || {}

/**
 * Create a custom event to open a winery's profile.
 *
 * @param  {Boolean}  isLoading
 * @return void
 */
window.pawine.openProfile = function(wineryId) {
  const event = new CustomEvent('openProfile', {
    detail: {
      wineryId,
    }
  })
  window.dispatchEvent(event)
}

let map

const app = createApp({
  mixins: [mapMixins],
  data() {
    return {
      routes: window.pawine.routes || {},
      wineries: window.pawine.wineries || [],
      mapMarkers: [],
      filteredWineries: [],
      selectedWineries: [],
      isLoading: false,
      isGeolocating: false,
      isSendingItineraryEmail: false,
      emailItineraryModal: false,
      searchLocation: {
        postalCode: null,
        latitude: null,
        longitude: null,
      },
      filterSelections: {
        postalCode: null,
        radius: 25,
        wineTypes: [],
        wineryExperience: null,
      },
      searchForm: {
        postalCode: null,
        radius: 25,
        wineTypes: [],
        wineryExperience: null,
      },
      sendItineraryForm: {
        name: null,
        email: null,
      }
    }
  },
  computed: {
    wineTypes() {
      let types = []

      this.wineries.forEach(winery => {
        if (winery.wineTypes) {
          winery.wineTypes.forEach(type => {
            types.push(type)
          })
        }
      })

      // Remove duplicates and sort alphabetically.
      return [... new Set(types)].sort()
    },
    wineryExperiences() {
      let experiences = []

      this.wineries.forEach(winery => {
        if (winery.wineryExperiences) {
          winery.wineryExperiences.forEach(experience => {
            experiences.push(experience)
          })
        }
      })

      // Remove duplicates and sort alphabetically.
      return [... new Set(experiences)].sort()
    },
    /**
     * Collect all the computed winery IDs.
     * @note additional location IDs follow this convention: [winery ID]-[location ID]
     */
    computedWineryIds() {
      return this.wineries.map(winery => {
        return (winery.wineryId ? `${winery.wineryId}-${winery.id}` : winery.id)
      })
    },
  },
  async mounted() {
    this.wineries = await this.loadWineries()
    this.filteredWineries = this.wineries

    // We create our own backdrop so that it's behind the modal.
    this.emailItineraryModal = new Modal(this.$refs['modal-email-itinerary'], {
      backdrop: false,
    })

    // Pre-select wineries from a URL param?
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const wineryIds = urlParams.get('wineryIds')
    if (wineryIds) {

      // Filter-out any invalid computed winery IDs.
      // @note additional location IDs follow this convention: [winery ID]-[location ID]
      this.selectedWineries = wineryIds.split(',').filter(wineryId => {

        if (wineryId.includes('-')) {
          return this.computedWineryIds.includes(wineryId)
        }

        return this.computedWineryIds.includes(parseInt(wineryId))
      })
    }

    this.initMap()
  },
  methods: {
    async loadWineries() {
      this.isLoading = true
      const resp = await axios.get(this.routes.loadWineries)
      this.isLoading = false

      return resp.data
    },
    async initMap() {
      const position = { lat: 40.913410, lng: -77.764748 }

      map = new Map(document.getElementById('map'), {
        zoom: 7,
        center: position,
        mapId: window.pawine.googleMapsId,
      })

      this.populateMap()
      this.addCurrentLocationToMap()
    },
    populateMap() {

      // First remove any existing markers.
      this.mapMarkers.forEach(marker => {
        google.maps.event.removeListener(marker.listener)
        marker.marker.setMap(null)
      })

      this.mapMarkers = []

      // Then add a new marker for each winery.
      this.filteredWineries.forEach(winery => {
        if (winery.latitude && winery.longitude) {

          let pinSvgString

          if (winery.isMember) {
            pinSvgString = window.pawine.img.memberMapPin
          } else {
            pinSvgString = window.pawine.img.nonMemberMapPin
          }

          const parser = new DOMParser()
          const pinSvg = parser.parseFromString(
            pinSvgString,
            "image/svg+xml",
          ).documentElement

          const marker = new AdvancedMarkerElement({
            map,
            position: { lat: Number(winery.latitude), lng: Number(winery.longitude) },
            title: winery.name,
            content: pinSvg,
          })

          const listener = marker.addListener('click', ({ domEvent, latLng }) => {
            const { target } = domEvent

            // Open info window.
            this.mapInfoWindow.close()
            this.mapInfoWindow.setContent(
              '<div class="map-ui-info-window">' + 
                '<b>' + winery.name + '</b><br>' + 
                winery.address + '<br>' + 
                winery.city + ', ' + winery.state + ' ' + winery.postalCode + '<br>' + 
                '<button type="button" onclick="window.pawine.openProfile(' + winery.id + ')">View Profile</button>' + 
              '</div>'
            )
            this.mapInfoWindow.open(marker.map, marker)

            // Scroll the winery into view within the search results panel.
            const locationResultId = 'winery-result-' + (winery.wineryId ? `${winery.wineryId}-${winery.id}` : winery.id)
            const locationResult = document.getElementById(locationResultId)
            const resultsContainer = document.getElementById('results')
            const scrollToPosition = locationResult.offsetTop - resultsContainer.scrollTop

            resultsContainer.scrollBy({top: scrollToPosition, left: 0, behavior: 'smooth'})
          })

          this.mapMarkers.push({
            marker,
            listener,
            winery,
          })
        }
      })

      // Recenter and zoom on markers.
      const bounds = new LatLngBounds()

      this.filteredWineries.forEach((winery) => {
        if (winery.latitude && winery.longitude) {
          bounds.extend({ lat: Number(winery.latitude), lng: Number(winery.longitude) })
        }
      })

      map.fitBounds(bounds)
    },
    async search() {
      this.filterSelections.postalCode = this.searchForm.postalCode
      this.filterSelections.wineTypes[0] = this.searchForm.wineTypes[0]
      this.filterSelections.wineTypes[1] = this.searchForm.wineTypes[1]
      this.filterSelections.wineryExperience = this.searchForm.wineryExperience

      this.filteredWineries = await this.filterWineries()
      this.populateMap()

      // Store the search.
      axios.post(this.routes.saveSearch, {
        ui: 'create-itinerary',
        parameters: this.filterSelections,
      })
    },
    async clearFilters() {
      this.searchForm.postalCode = null
      this.searchForm.wineTypes = []

      this.filterSelections.postalCode = null
      this.filterSelections.wineTypes = []

      this.filteredWineries = await this.filterWineries()
    },
    async filterWineries() {
      let filtered = cloneDeep(this.wineries)

      // Wine Types
      this.searchForm.wineTypes.forEach(wineType => {
        if (wineType) {
          filtered = filtered.filter(winery => {
            return winery.wineTypes && winery.wineTypes.includes(wineType)
          })
        }
      })

      // Winery Experience
      if (this.searchForm.wineryExperience) {
        filtered = filtered.filter(winery => {
          return winery.wineryExperiences && winery.wineryExperiences.includes(this.searchForm.wineryExperience)
        })
      }

      // Postal code search
      if (this.filterSelections.postalCode) {

        if (
          this.filterSelections.postalCode != this.searchLocation.postalCode || (
            ! this.searchLocation.latitude || 
            ! this.searchLocation.longitude
          )
        ) {
          await this.getPostalCodeCoords(this.filterSelections.postalCode)
        }

        filtered = filtered.filter(winery => {

          if (! winery.latitude || ! winery.longitude) {
            return false
          }

          // Calculate the distance, in miles, between the provided postal code 
          // and the ad's postal code.
          const distance = 
            this.radiansToDegrees(Math.acos(
              Math.sin(this.degreesToRadians(winery.latitude)) *
              Math.sin(this.degreesToRadians(this.searchLocation.latitude))
              + Math.cos(this.degreesToRadians(winery.latitude))
              * Math.cos(this.degreesToRadians(this.searchLocation.latitude))
              * Math.cos(this.degreesToRadians(winery.longitude - this.searchLocation.longitude))
            )) * 69.06

          return distance <= this.searchForm.radius
        })
      }

      return filtered
    },
    async addCurrentLocationToMap() {
      await this.getUserCoords()

      if (this.userCoords.latitude && this.userCoords.longitude) {
        let pinSvgString = window.pawine.img.currentLocationMapPin

        const parser = new DOMParser()
        const pinSvg = parser.parseFromString(
          pinSvgString,
          "image/svg+xml",
        ).documentElement

        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: Number(this.userCoords.latitude), lng: Number(this.userCoords.longitude) },
          title: 'Current Location',
          content: pinSvg,
        })

        const listener = marker.addListener('click', ({ domEvent, latLng }) => {
          const { target } = domEvent

          this.mapInfoWindow.close()
          this.mapInfoWindow.setContent(
            '<b>Current Location</b>'
          )
          this.mapInfoWindow.open(marker.map, marker)
        })

        this.mapMarkers.push({
          marker,
          listener,
        })
      }
    },
    openEmailItineraryModal() {
      this.emailItineraryModal.show()

      this.$refs['modal-email-itinerary-backdrop'].classList.add('show')
      this.$refs['modal-email-itinerary-backdrop'].classList.remove('pe-none')
    },
    closeEmailItineraryModal() {
      this.emailItineraryModal.hide()

      this.$refs['modal-email-itinerary-backdrop'].classList.remove('show')
      this.$refs['modal-email-itinerary-backdrop'].classList.add('pe-none')
    },
    async sendEmail() {
      this.isSendingItineraryEmail = true

      try {

        const resp = await axios.post(this.routes.sendEmail, {
          name: this.sendItineraryForm.name,
          email: this.sendItineraryForm.email,
          wineryIds: this.selectedWineries,
        })

        let div = document.createElement('div')
        div.innerHTML = `<div class="alert alert-success text-start">Your itinerary was successfully sent to ${this.sendItineraryForm.email}.</div>`
        swal({
          content: div
        })

        this.closeEmailItineraryModal()
        this.isSendingItineraryEmail = false

      } catch (error) {
        console.error(error)
        this.isSendingItineraryEmail = false

        const statusCode = error?.response?.status
        if (statusCode == 419) {
          let div = document.createElement('div');
          div.innerHTML = '<div class="text-danger">Your session has expired. Please reload the page to continue creating your itinerary.</div>'
          swal({
            content: div,
            buttons: [null, 'Reload'],
          })
          .then((reload) => {
            if (reload) {
              window.location.href = '/create-itinerary?wineryIds=' + this.selectedWineries
            }
          })

          return
        }

        if (error?.response?.data?.errors) {
          let errors = []
          for (let e in error.response.data.errors) {
            errors.push(error.response.data.errors[e][0])
          }

          let div = document.createElement('div');
          div.innerHTML = '<ul class="alert alert-danger text-start"><li>' + errors.join('</li><li>') + '</li></ul>'
          swal({
            icon: 'error',
            content: div,
          })
        } else {
          swal('An unexpected error occurred.')
        }
      }
    }
  }
})

app.mount('#map-ui')
